import { ENVIRONMENT } from '@monetize/ui/config';
import useResizeObserver from '@react-hook/resize-observer';
import type { PDFDocumentProxy } from 'pdfjs-dist/types/src/display/api';
import {
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { MdOutlineFileDownload } from 'react-icons/md';
import { pdfjs } from 'react-pdf';
import { MBox, MCustomIconButton, MFlex, MSpinner, MText } from '..';
import { handleApiErrorToast } from '../../../api/axios';
import { getSafeFilename } from '../../../utils';
import { downloadBlobAsFile } from '../../../utils/download';
import PdfDocument from '../Pdf/PdfDocument';

pdfjs.GlobalWorkerOptions.workerSrc = ENVIRONMENT.PDFJS_WORKER_URL;

/**
 * Highlights text in PDF
 *
 * https://github.com/wojtekmaj/react-pdf/wiki/Recipes#highlight-text-on-the-page
 * https://gist.github.com/wojtekmaj/f265f55e89ccb4ce70fbd2f8c7b1d95d
 *
 * @param text
 * @param pattern
 * @returns
 */
function highlightPattern(text: string, pattern: string | RegExp): any {
  const splitText = text.split(pattern);
  if (splitText.length <= 1) {
    return text;
  }

  const matches = text.match(pattern);
  return splitText.reduce((arr: any[], element, index) => {
    return matches && matches[index]
      ? [...arr, element, <mark key={index}>{matches[index]}</mark>]
      : [...arr, element];
  }, []);
}

interface PreviewPdfProps {
  /** Filename to use when pdf is downloaded */
  filename: string;
  /**
   * Highlight text that matches provided regex
   * /Docusign tags: /\/m[0-9a-z]{3}\//i
   * https://monetizenow.atlassian.net/wiki/spaces/PE/pages/1159004161/Docusign+Tabs
   */
  highlightTextPattern?: string | RegExp;
  currentPdfFile: ArrayBuffer | null;
  showDownloadButton?: boolean;
  isLoading?: boolean;
}

/**
 * Side drawer for previewing PDF documents
 */
export const PreviewPdf: FC<PreviewPdfProps> = ({
  filename,
  highlightTextPattern,
  currentPdfFile,
  showDownloadButton,
  isLoading,
}) => {
  const bodyRef = useRef<HTMLDivElement>(null);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [size, setSize] = useState<DOMRect>();
  const [clonedPdf, setClonedPdf] = useState<ArrayBuffer>();

  useEffect(() => {
    if (currentPdfFile) {
      setClonedPdf(currentPdfFile.slice(0));
    }
  }, [currentPdfFile]);

  // Add debouncing/size comparison to prevent unnecessary updates
  useResizeObserver(bodyRef, (entry) => {
    const newRect = entry.contentRect;
    // Only update if the width actually changed by at least 1px
    if (Math.abs((size?.width || 0) - newRect.width) >= 10) {
      setSize(newRect);
    }
  });

  useLayoutEffect(() => {
    if (bodyRef.current) {
      const roundingRect = bodyRef.current.getBoundingClientRect();
      roundingRect && setSize(roundingRect);
    }
  }, [bodyRef]);

  /**
   * Allows highlighting of text within PDF
   * https://github.com/wojtekmaj/react-pdf/wiki/Recipes#highlight-text-on-the-page
   */
  const textRenderer = useCallback(
    (
      textItem: {
        pageIndex: number;
        pageNumber: number;
        itemIndex: number;
      } & { str: string },
    ) => {
      if (!highlightTextPattern) {
        return textItem.str;
      }
      return highlightPattern(textItem.str, highlightTextPattern);
    },
    [highlightTextPattern],
  );

  const onDocumentLoadSuccess = (pdf: PDFDocumentProxy) => {
    setNumPages(pdf?.numPages || 0);
  };

  const downloadPdf = async () => {
    try {
      await downloadBlobAsFile(
        currentPdfFile,
        `${getSafeFilename(filename)}.pdf`,
      );
    } catch (err) {
      handleApiErrorToast(err);
    }
  };

  if (isLoading) {
    return <MSpinner />;
  }

  return (
    <MBox
      position="relative"
      backgroundColor="tGray.sidebarDark"
      ref={bodyRef}
      w="100%"
    >
      <MFlex
        top={0}
        right={0}
        zIndex={1}
        w="100%"
        background="tGray.sidebarDark"
      >
        <MFlex my="2" w="100%" alignItems="center" mx="2">
          <MText
            fontSize="md"
            color="tPurple.500"
            fontWeight="bold"
            mr={6}
            flex="1"
          >
            {filename}
          </MText>

          {showDownloadButton && clonedPdf && (
            <MCustomIconButton
              containerSize="6"
              btnSize={5}
              fontSize="lg"
              icon={MdOutlineFileDownload}
              onClick={() => downloadPdf()}
              aria-label="Download PDF"
              isDisabled={!clonedPdf}
            />
          )}
        </MFlex>
      </MFlex>

      {!isLoading && clonedPdf && (
        <MFlex flexDir="column" pt="6" overflowY="scroll" maxHeight="500px">
          <PdfDocument
            currentPdfFile={clonedPdf}
            onDocumentLoadSuccess={onDocumentLoadSuccess}
            textRenderer={textRenderer}
            numPages={numPages}
            size={size?.width ? size.width - 50 : 1105}
          />
        </MFlex>
      )}
    </MBox>
  );
};
